/**
 * MasterPatternView — root component for master data management.
 *
 * Composes all sub-systems: store context, toolbar, list, form panel,
 * and modals. Exposes imperative ref API via MasterPatternViewRef.
 *
 * @module components/MasterPatternView
 * @see FMPV-MVWC-E003-S006 I-001
 */
import React, { forwardRef, useCallback, useImperativeHandle, useEffect, useLayoutEffect, useMemo, useState, useRef } from 'react';
import '../master-pattern-view.css';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  MagnifyingGlassIcon as SearchIcon,
  FunnelIcon as FilterIcon,
  ChevronDownIcon,
  EllipsisVerticalIcon,
  DocumentArrowDownIcon,
  ArrowUpTrayIcon,
  ListBulletIcon,
  Squares2X2Icon,
  ClockIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Building2 } from 'lucide-react';
import { AuditHistoryPanel } from '../../../audit/components/AuditHistoryPanel';
import { Button } from '../../Button/Button';
import { Switch } from '../../Switch/Switch';
import type { MasterPatternViewEntity } from '../types';
import type { MasterPatternViewProps, MasterPatternViewRef } from '../types';
import type { MasterPatternViewDefinition } from '../types/definition.types';
import { MasterViewProvider } from '../store/MasterViewContext';
import { useMasterViewStore } from '../store/MasterViewContext';
import { ConfigTableContent } from './ConfigTableContent';
import { MasterPatternViewForm } from './form/MasterPatternViewForm';
import { getAllFields } from '../engine/fieldEngine';
import { FilterPanel } from './toolbar/FilterPanel';
import { ToolbarActions } from './toolbar/ToolbarActions';
import { ListTable } from './list/ListTable';
import { ListCard } from './list/ListCard';
import { EmptyState } from './list/EmptyState';
import { PaginationBar } from './list/PaginationBar';
import { BatchBar } from './list/BatchBar';
import { DirtyStateDialog } from './modals/DirtyStateDialog';
import { ChangeStatusModal } from './modals/ChangeStatusModal';
import { ConflictDialog } from './modals/ConflictDialog';
import { CompanyEnableModal } from './modals/CompanyEnableModal';
import { NotEnabledModal } from './modals/NotEnabledModal';
import type { NotEnabledModalTrigger } from './modals/NotEnabledModal';
import { useMasterList } from '../hooks/useMasterList';
import { useMasterForm } from '../hooks/useMasterForm';
import { useChangeStatus } from '../hooks/useChangeStatus';
import { useSortState } from '../hooks/useSortState';
import { useToast } from '../hooks/useToast';
import { CompanySelector } from './toolbar/CompanySelector';
import { useMasterPatternViewTranslation } from '../hooks/useMasterPatternViewTranslation';
import { internalTranslations } from '../i18n/internalTranslations';

import { InternalToastContainer } from './InternalToastContainer';

const queryClient = new QueryClient();

function MasterPatternViewInner<T extends MasterPatternViewEntity>(
  props: MasterPatternViewProps<T>,
  ref: React.ForwardedRef<MasterPatternViewRef>,
) {
  const {
    definition,
    service,
    configService,
    renderConfigBody,
    renderToolbarActions,
    renderActionsMenuItems,
    renderBody,
    title,
    entityName,
    permissions,
    pagination,
    selectable,
    batchActions,
    autoLoad = false,
    defaultFiltersOpen = false,
    showExport,
    showImport,
    onExport,
    onImport,
    globalActions,
    auditHistory,
    tabs,
    headerFields,
    navigationType = 'sidebar',
    showBackButton = true,
    formWidth,
    formHeight,
    openRecordId,
    openRecordMode,
    onRecordOpen,
    onRecordClose,
    onRecordSaved,
    isLoading: externalIsLoading = false,
    validate,
    onCreateSuccess,
    onUpdateSuccess,
    onStatusChangeSuccess,
    onError,
    toast: toastProp,
    showInternalToasts = true,
    companies,
    defaultCompany,
    onCompanyChange,
    renderForm,
    detailTabs,
    defaultCreateValues,
    showViewToggle,
    renderCardContent,
    recordTitleFields,
    defaultActions,
    canEdit,
    showCompanySelector,
    customSave,
    onFormSave,
    externalIsDirty,
    locale,
    t: tProp,
    uiPrefix,
    fieldsPrefix,
    externalTranslations,
  } = props;

  // ── Motor de traducción ──────────────────────────────────────────────────
  const { t } = useMasterPatternViewTranslation({
    locale,
    uiPrefix,
    fieldsPrefix,
    externalTranslations,
    internalTranslations,
    tProp,
  });

  // Store's own DEFAULT_SEARCH_PARAMS.pageSize (listSlice.ts) is a fixed 20 — this is
  // the actual value `pagination.defaultPageSize` (documented default: 10, same as
  // `pageSizeOptions`'s first entry) needs to seed the store with at creation, or a
  // custom `defaultPageSize` a consumer passes is silently ignored: the page-size
  // selector still shows it as an option, but the list fetches (and the store reports)
  // 20 rows from the very first load, before the user ever touches the selector.
  const initialPageSize = pagination === false ? undefined : (pagination?.defaultPageSize ?? 10);

  // CONFIG_TABLE: toolbar + company context + renderConfigBody
  if (definition.type === 'CONFIG_TABLE') {
    return (
      <MasterViewProvider<T> definition={definition}>
        <ConfigTableContent
          definition={definition}
          configService={configService}
          renderConfigBody={renderConfigBody}
          renderToolbarActions={renderToolbarActions}
          renderActionsMenuItems={renderActionsMenuItems}
          title={title}
          companies={companies}
          defaultCompany={defaultCompany}
          onCompanyChange={onCompanyChange}
          toast={toastProp}
        />
      </MasterViewProvider>
    );
  }

  // CUSTOM: toolbar + company context + renderBody (consumer owns all state)
  if (definition.type === 'CUSTOM') {
    return (
      <MasterViewProvider<T> definition={definition}>
        <CustomContent
          definition={definition}
          renderBody={renderBody}
          title={title}
          companies={companies}
          defaultCompany={defaultCompany}
          onCompanyChange={onCompanyChange}
          toast={toastProp}
          showCompanySelector={showCompanySelector}
          customSave={customSave}
          renderActionsMenuItems={renderActionsMenuItems}
          renderToolbarActions={renderToolbarActions}
          t={t}
        />
      </MasterViewProvider>
    );
  }

  return (
    <MasterViewProvider<T> definition={definition} defaultCreateValues={defaultCreateValues} initialPageSize={initialPageSize}>
      <MasterPatternViewContent<T>
        ref={ref}
        definition={definition}
        service={service!}
        title={title}
        entityName={entityName ?? ''}
        permissions={permissions}
        pagination={pagination}
        selectable={selectable}
        batchActions={batchActions}
        autoLoad={autoLoad}
        defaultFiltersOpen={defaultFiltersOpen}
        showExport={showExport}
        showImport={showImport}
        onExport={onExport}
        onImport={onImport}
        globalActions={globalActions}
        auditHistory={auditHistory}
        tabs={tabs}
        headerFields={headerFields}
        navigationType={navigationType}
        showBackButton={showBackButton}
        formWidth={formWidth}
        formHeight={formHeight}
        openRecordId={openRecordId}
        openRecordMode={openRecordMode}
        onRecordOpen={onRecordOpen}
        onRecordClose={onRecordClose}
        onRecordSaved={onRecordSaved}
        externalIsLoading={externalIsLoading}
        validate={validate}
        onCreateSuccess={onCreateSuccess}
        onUpdateSuccess={onUpdateSuccess}
        onStatusChangeSuccess={onStatusChangeSuccess}
        onError={onError}
        toast={toastProp}
        showInternalToasts={showInternalToasts}
        companies={companies}
        defaultCompany={defaultCompany}
        onCompanyChange={onCompanyChange}
        renderForm={renderForm}
        detailTabs={detailTabs}
        defaultCreateValues={defaultCreateValues}
        showViewToggle={showViewToggle}
        renderCardContent={renderCardContent}
        recordTitleFields={recordTitleFields}
        defaultActions={defaultActions}
        canEdit={canEdit}
        onFormSave={onFormSave}
        externalIsDirty={externalIsDirty}
        t={t}
      />
    </MasterViewProvider>
  );
}

interface ContentProps<T extends MasterPatternViewEntity>
  extends Omit<MasterPatternViewProps<T>, 'isLoading' | 'service' | 'entityName'> {
  externalIsLoading: boolean;
  /** Required for GLOBAL / UNIVERSAL / COMPANY_SPECIFIC — guaranteed non-null by MasterPatternViewInner branching */
  service: import('../types/service.types').MasterPatternViewService<T>;
  entityName: string;
}

const MasterPatternViewContent = forwardRef(function MasterPatternViewContentInner<
  T extends MasterPatternViewEntity,
>(
  {
    definition,
    service,
    title,
    entityName,
    permissions,
    pagination,
    selectable,
    batchActions,
    autoLoad,
    defaultFiltersOpen,
    showExport,
    showImport,
    onExport,
    onImport,
    globalActions,
    auditHistory,
    tabs,
    headerFields,
    navigationType,
    showBackButton,
    formWidth,
    formHeight,
    openRecordId,
    openRecordMode = 'editing',
    onRecordOpen,
    onRecordClose,
    onRecordSaved,
    externalIsLoading,
    validate,
    onCreateSuccess,
    onUpdateSuccess,
    onStatusChangeSuccess,
    onError,
    toast: toastProp,
    showInternalToasts = true,
    companies,
    defaultCompany,
    onCompanyChange,
    renderForm,
    detailTabs,
    defaultCreateValues,
    showViewToggle = true,
    renderCardContent,
    recordTitleFields,
    defaultActions,
    canEdit,
    onFormSave,
    externalIsDirty,
    t = (key: string, defaultValue?: string) => defaultValue ?? key,
  }: ContentProps<T>,
  ref: React.ForwardedRef<MasterPatternViewRef>,
) {
  const data = useMasterViewStore((s) => s.data);
  const total = useMasterViewStore((s) => s.total);
  const isLoading = useMasterViewStore((s) => s.isLoading);
  const hasSearched = useMasterViewStore((s) => s.hasSearched);
  const searchParams = useMasterViewStore((s) => s.searchParams);
  const selectedIds = useMasterViewStore((s) => s.selectedIds);
  const isDirty = useMasterViewStore((s) => s.isDirty);
  const mode = useMasterViewStore((s) => s.mode);
  const activeRecord = useMasterViewStore((s) => s.activeRecord);
  const initialCreateValues = useMasterViewStore((s) => s.initialCreateValues);
  const pendingAction = useMasterViewStore((s) => s.pendingAction);
  const openCreate = useMasterViewStore((s) => s.openCreate);
  const openEdit = useMasterViewStore((s) => s.openEdit);
  const values = useMasterViewStore((s) => s.values);
  const setValues = useMasterViewStore((s) => s.setValues);
  const setInitialCreateValues = useMasterViewStore((s) => s.setInitialCreateValues);

  // Sync defaultCreateValues into form when in creating mode and values are async-loaded
  // (e.g. a company → plan → currency lookup that resolves after openCreate() already ran
  // with an empty snapshot). A late-arriving default is still a default, not a user edit — so
  // it's merged into initialCreateValues too, not just values. Without this, the dirty-check
  // baseline never learns about it and the field reads as "changed" (empty → value) the instant
  // it lands, even though nothing was touched.
  useEffect(() => {
    if (mode === 'creating' && defaultCreateValues) {
      const lateDefaults = Object.fromEntries(
        Object.entries(defaultCreateValues).filter(
          ([key, val]) => val !== undefined && values[key] === undefined,
        ),
      );
      if (Object.keys(lateDefaults).length > 0) {
        setValues({ ...values, ...lateDefaults });
        setInitialCreateValues(lateDefaults);
      }
    }
  }, [mode, defaultCreateValues]);
  const openView = useMasterViewStore((s) => s.openView);
  const updateActiveRecord = useMasterViewStore((s) => s.updateActiveRecord);
  const setIsLoadingRecord = useMasterViewStore((s) => s.setIsLoadingRecord);
  const setRecordLoadFailed = useMasterViewStore((s) => s.setRecordLoadFailed);
  const closeForm = useMasterViewStore((s) => s.closeForm);
  const setIsDirty = useMasterViewStore((s) => s.setIsDirty);
  const clearSelection = useMasterViewStore((s) => s.clearSelection);
  const setSearchParams = useMasterViewStore((s) => s.setSearchParams);
  const isDirtyStateDialogOpen = useMasterViewStore((s) => s.isDirtyStateDialogOpen);
  const openDirtyStateDialog = useMasterViewStore((s) => s.openDirtyStateDialog);
  const closeDirtyStateDialog = useMasterViewStore((s) => s.closeDirtyStateDialog);
  const isChangeStatusModalOpen = useMasterViewStore((s) => s.isChangeStatusModalOpen);
  const openChangeStatusModal = useMasterViewStore((s) => s.openChangeStatusModal);
  const closeChangeStatusModal = useMasterViewStore((s) => s.closeChangeStatusModal);
  const clearPendingAction = useMasterViewStore((s) => s.clearPendingAction);
  const setPendingAction = useMasterViewStore((s) => s.setPendingAction);
  const isCompanyEnableModalOpen = useMasterViewStore((s) => s.isCompanyEnableModalOpen);
  const openCompanyEnableModal = useMasterViewStore((s) => s.openCompanyEnableModal);
  const closeCompanyEnableModal = useMasterViewStore((s) => s.closeCompanyEnableModal);

  // Filter state
  const isFilterPanelOpen = useMasterViewStore((s) => s.isFilterPanelOpen);
  const activeFilters = useMasterViewStore((s) => s.activeFilters);
  const toggleFilterPanel = useMasterViewStore((s) => s.toggleFilterPanel);
  const setActiveFilters = useMasterViewStore((s) => s.setActiveFilters);
  const clearFilters = useMasterViewStore((s) => s.clearFilters);


  // Company context state
  const activeCompanyContext = useMasterViewStore((s) => s.activeCompanyContext);
  const setActiveCompanyContext = useMasterViewStore((s) => s.setActiveCompanyContext);
  const setGlobalValues = useMasterViewStore((s) => s.setGlobalValues);

  // View mode toggle (table / cards)
  const viewMode = useMasterViewStore((s) => s.viewMode);
  const setViewMode = useMasterViewStore((s) => s.setViewMode);

  // Auto-switch to cards on narrow viewports — mirrors MasterCrud.tsx:869-875 exactly
  // (same breakpoint, same matchMedia 'change' listener). Only fires when the
  // breakpoint is actually crossed, so a manual toggle click still sticks until then.
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1024px)');
    const handleDeviceChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setViewMode(e.matches ? 'cards' : 'table');
    };
    handleDeviceChange(mediaQuery);
    mediaQuery.addEventListener('change', handleDeviceChange);
    return () => mediaQuery.removeEventListener('change', handleDeviceChange);
  }, [setViewMode]);

  // Two storages, two different keys, two different jobs:
  // - sessionStorage (`companySelected`): THIS tab's own last active company. Survives an F5
  //   of this same tab (sessionStorage isn't cleared by a reload, only by closing the tab) and
  //   is never shared with any other tab. The master ALWAYS reads from here, never straight
  //   from localStorage — see the seeding step below for how a brand new tab still inherits
  //   a value.
  // - localStorage (`lastCompanySelected`): shared across every tab of the same origin, on
  //   purpose — it's what lets a BRAND NEW tab (whose sessionStorage always starts empty)
  //   inherit the last company used anywhere, instead of always starting from Global/first.
  // 'global' is stored as the literal string 'global' in both, not as an absent key — that way
  // reading the key unambiguously distinguishes "last selection was Global" from "nothing was
  // ever selected".
  const SESSION_COMPANY_KEY = 'companySelected';
  const LOCAL_LAST_COMPANY_KEY = 'lastCompanySelected';

  // Seed sessionStorage from localStorage as early as possible — independent of `companies`,
  // which usually arrives async (a fresh tab has no cache, so it's typically empty/undefined
  // on this very first render). Doing the seed here, decoupled from the restore effect below,
  // means it's in place before anything else reads sessionStorage.
  useEffect(() => {
    try {
      if (sessionStorage.getItem(SESSION_COMPANY_KEY) === null) {
        const localStored = localStorage.getItem(LOCAL_LAST_COMPANY_KEY);
        if (localStored !== null) sessionStorage.setItem(SESSION_COMPANY_KEY, localStored);
      }
    } catch {
      // storage unavailable (SSR, private browsing) — nothing to seed.
    }
  }, []);

  // Restore the active company context from sessionStorage (already seeded above if needed).
  // GLOBAL type defaults to 'global' (set by overrideSlice) — don't override with first company,
  // unless a previously-selected company was persisted and still exists.
  // COMPANY type defaults to the first available company, same override applies.
  //
  // `companies` is a host-provided prop that's frequently still empty/undefined on first mount
  // (it's usually the result of an async fetch, and a brand new tab has no cache to prime it
  // with) — an effect that only ran once on mount would see an empty list, do nothing, and
  // never get a second chance once `companies` actually arrives, silently dropping the restore.
  // So this effect depends on `companies` and keeps retrying every time it changes, but only
  // ever APPLIES the restore once (guarded by the ref) — later `companies` updates (refetches,
  // etc.) must not clobber a selection the user has since made manually.
  const didRestoreCompanyRef = useRef(false);
  useEffect(() => {
    if (didRestoreCompanyRef.current) return;
    if (!companies || companies.length === 0) return;
    didRestoreCompanyRef.current = true;

    let sessionStored: string | null = null;
    try {
      sessionStored = sessionStorage.getItem(SESSION_COMPANY_KEY);
    } catch {
      // sessionStorage unavailable (SSR, private browsing).
    }

    const isValidCompanyId = (id: string | null): id is string =>
      !!id && id !== 'global' && companies.some((c) => c.id === id);
    const restoredId = isValidCompanyId(sessionStored) ? sessionStored : null;

    // `defaultCompany` beats sessionStorage, not the other way around. A host that explicitly
    // passes `defaultCompany` is making an authoritative, freshly-computed claim for THIS
    // specific mount (e.g. a lookup's "+" inheriting the record it was opened from, or a
    // company already established elsewhere in this same tab's session) — honoring a stored
    // value over that turns "open a lookup" into "silently re-scope to whatever storage
    // happens to say right now". sessionStorage stays the fallback for the one case it's
    // actually meant for: a fresh mount with no more specific answer available.
    if (definition.type === 'COMPANY_SPECIFIC') {
      const initialCompany = defaultCompany ?? (restoredId ? companies.find((c) => c.id === restoredId)! : companies[0]);
      setActiveCompanyContext(initialCompany.id);
      // El selector visual restaura la compañía desde storage, pero eso solo
      // actualiza el store interno de MPV — el consumidor (onCompanyChange) nunca se
      // entera salvo que dispare un clic manual en el dropdown. Sin este aviso, el
      // formulario/tabs custom del consumidor quedan pensando que no hay compañía
      // seleccionada aunque el chip ya muestre una.
      onCompanyChange?.(initialCompany);
    } else if (definition.type === 'GLOBAL') {
      // GLOBAL still defaults to 'global' when neither defaultCompany nor a persisted
      // company selection is available (including when the stored value IS 'global') —
      // only set it when one of the two actually resolves to a specific company.
      const initialCompany = defaultCompany ?? (restoredId ? companies.find((c) => c.id === restoredId) : undefined);
      if (initialCompany) {
        setActiveCompanyContext(initialCompany.id);
        onCompanyChange?.(initialCompany);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies]);

  // Persist the active company context in BOTH storages on every change — sessionStorage so a
  // reload of THIS tab restores its own selection (never another tab's), and localStorage so a
  // brand new tab still inherits the last company used anywhere. Both storages get the same
  // literal value, including 'global'. See the read side above for the full rationale.
  //
  // Gate on `didRestoreCompanyRef` — NOT just `!!activeCompanyContext` — because on the very
  // first render `activeCompanyContext` is already non-null: the Zustand store's own initial
  // state (see overrideSlice) sets it straight to 'global' for GLOBAL-type masters, well before
  // `companies` has loaded and the restore effect above gets its one shot at correcting it to
  // the real stored company. Without this guard, THIS effect fires on that very first commit —
  // right after the seed effect freshly copied the real company from localStorage into
  // sessionStorage — and stomps both storages back to the placeholder 'global' default,
  // permanently losing the actual last-used company before restore ever ran.
  useEffect(() => {
    if (!activeCompanyContext) return;
    if (!didRestoreCompanyRef.current) return;
    try {
      sessionStorage.setItem(SESSION_COMPANY_KEY, activeCompanyContext);
    } catch {
      // sessionStorage unavailable — persistence is a nice-to-have, not required.
    }
    try {
      localStorage.setItem(LOCAL_LAST_COMPANY_KEY, activeCompanyContext);
    } catch {
      // localStorage unavailable — persistence is a nice-to-have, not required.
    }
  }, [activeCompanyContext]);

  const { showSuccess, showError } = useToast(toastProp, showInternalToasts);

  const [actionsDropdownOpen, setActionsDropdownOpen] = useState(false);
  // Persistente entre compañías REALES a propósito — es una preferencia de vista
  // ("solo quiero ver lo específico de la compañía activa"), no algo ligado a UNA
  // compañía en particular. El filtro __view no codifica ningún id de compañía —
  // solo dice "acota a la compañía activa", así que sigue siendo válido tal cual al
  // pasar de una compañía a otra; no hace falta resetear nada ahí (igual que
  // "Mostrar datos globales", que nunca se reseteó y nadie lo echó en falta).
  const [showOnlyCompany, setShowOnlyCompany] = useState(false);

  // Excepción: sí se resetea al volver a 'global'. El toggle ni siquiera se muestra en
  // contexto Global (showMostrarSoloCompania exige isCompanyCtxActive) y el filtro
  // __view:'company' no tiene compañía a la cual acotarse ahí — dejarlo pegado hacía
  // que la búsqueda en Global no devolviera resultados.
  useEffect(() => {
    if (activeCompanyContext === 'global') {
      setShowOnlyCompany(false);
      setSearchParams({ filters: searchParams.filters.filter((f) => f.field !== '__view'), page: 1 });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompanyContext]);

  // NotEnabledModal state — triggered when a GLOBAL record is not yet assigned to the active company
  const [notEnabledState, setNotEnabledState] = useState<{
    isOpen: boolean;
    trigger: NotEnabledModalTrigger;
    record: T | null;
    isLoading: boolean;
  }>({ isOpen: false, trigger: 'OPEN_EDIT', record: null, isLoading: false });

  // Pending company ID for COMPANY_SWITCH trigger — the company the user is trying to switch to
  const [pendingCompanySwitchId, setPendingCompanySwitchId] = useState<string | null>(null);

  const closeNotEnabled = useCallback(() => {
    setNotEnabledState({ isOpen: false, trigger: 'OPEN_EDIT', record: null, isLoading: false });
    setPendingCompanySwitchId(null);
  }, []);

  const [companyEnableRecord, setCompanyEnableRecord] = useState<T | null>(null);
  const [assignedCompanyIds, setAssignedCompanyIds] = useState<string[]>([]);
  const [isCompanyEnableLoading, setIsCompanyEnableLoading] = useState(false);

  // Per-company enablement for the record CURRENTLY OPEN in the form — separate from
  // `assignedCompanyIds` above (which tracks whatever record the "Habilitar por compañía"
  // modal was opened for, not necessarily the same one). Drives the "Habilitado"/"No
  // habilitado" indicator in the form's own CompanySelector dropdown.
  const [formCompanyIds, setFormCompanyIds] = useState<string[]>([]);
  useEffect(() => {
    if (definition.type !== 'GLOBAL' || !service.getAssignedCompanyIds) {
      setFormCompanyIds([]);
      return;
    }
    if ((mode !== 'editing' && mode !== 'viewing') || !activeRecord) {
      setFormCompanyIds([]);
      return;
    }
    let cancelled = false;
    const id = (activeRecord as T & { id: string }).id;
    service.getAssignedCompanyIds(id).then((ids) => {
      if (!cancelled) setFormCompanyIds(ids);
    }).catch(() => {
      if (!cancelled) setFormCompanyIds([]);
    });
    return () => {
      cancelled = true;
    };
  }, [mode, activeRecord, definition.type, service]);

  // "Auditoría de Cambios" — mirrors MasterCrud's auditModalState (MasterCrud.tsx:591, 2112-2131)
  const [auditModalState, setAuditModalState] = useState<{ show: boolean; record: T | null }>({ show: false, record: null });
  const openAuditModal = useCallback((record: T) => setAuditModalState({ show: true, record }), []);
  const closeAuditModal = useCallback(() => setAuditModalState({ show: false, record: null }), []);
  // Tracks whether CompanyEnableModal was opened as a post-create step.
  // When true, closing or confirming the modal must also close the form.
  const isPostCreateModalRef = useRef(false);
  const { sortBy, sortOrder, setSort } = useSortState();
  const queryClient = useQueryClient();

  // Derive pagination config early — needed by useMasterList (mode) and IntersectionObserver
  const paginationConfig = pagination === false
    ? null
    : { mode: 'pages' as const, defaultPageSize: 10, pageSizeOptions: [10, 25, 50], ...(pagination || {}) };

  // Main list hook
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = useMasterList({
    service,
    entityName,
    autoLoad,
    defaultFiltersOpen,
    permissions,
    masterType: definition.type as 'UNIVERSAL' | 'COMPANY_SPECIFIC' | 'GLOBAL',
    toast: toastProp,
    showInternalToasts,
    mode: paginationConfig?.mode ?? 'pages',
    t,
  });

  // ── Infinite scroll ─────────────────────────────────────────────────────────
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hasNextPageRef = useRef(hasNextPage);
  const isFetchingNextPageRef = useRef(isFetchingNextPage);
  const fetchNextPageRef = useRef(fetchNextPage);
  useEffect(() => { hasNextPageRef.current = hasNextPage; }, [hasNextPage]);
  useEffect(() => { isFetchingNextPageRef.current = isFetchingNextPage; }, [isFetchingNextPage]);
  useEffect(() => { fetchNextPageRef.current = fetchNextPage; }, [fetchNextPage]);

  // Saved scroll position for restoration after data appends.
  const savedScrollTopRef = useRef(0);

  // Single scroll listener: saves position + triggers next page near bottom.
  // Using onScroll is more reliable than IntersectionObserver for internal
  // overflow-auto containers because IntersectionObserver requires the root
  // to already have its final size at observer-creation time.
  useEffect(() => {
    if (paginationConfig?.mode !== 'infinite') return;
    const container = scrollContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      savedScrollTopRef.current = container.scrollTop;
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (
        scrollHeight - scrollTop - clientHeight < 200 &&
        hasNextPageRef.current &&
        !isFetchingNextPageRef.current
      ) {
        fetchNextPageRef.current();
      }
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [paginationConfig?.mode]);

  // Auto-fill: after each data update, if the content is shorter than the
  // container there is nothing to scroll — load the next page automatically.
  useEffect(() => {
    if (paginationConfig?.mode !== 'infinite') return;
    const container = scrollContainerRef.current;
    if (!container) return;
    if (
      container.scrollHeight <= container.clientHeight &&
      hasNextPageRef.current &&
      !isFetchingNextPageRef.current
    ) {
      fetchNextPageRef.current();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, paginationConfig?.mode]);

  // Restore scroll position after data appends (prevents jump-to-top).
  useLayoutEffect(() => {
    if (paginationConfig?.mode !== 'infinite') return;
    const container = scrollContainerRef.current;
    if (!container || savedScrollTopRef.current === 0) return;
    container.scrollTop = savedScrollTopRef.current;
  }, [data, paginationConfig?.mode]);

  /**
   * Internal create-success handler.
   * For GLOBAL type in global context: form stays open, CompanyEnableModal opens
   * on top so the user can assign companies right after saving.
   * closeForm() is deferred to the modal's onClose/onConfirm handlers.
   */
  const handleCreateSuccess = useCallback(
    (record: T) => {
      if (
        definition.type === 'GLOBAL' &&
        (!activeCompanyContext || activeCompanyContext === 'global')
      ) {
        isPostCreateModalRef.current = true;
        setCompanyEnableRecord(record);
        setAssignedCompanyIds([]); // brand-new record, no companies assigned yet
        openCompanyEnableModal();
      }
      onCreateSuccess?.(record);
      onRecordSaved?.(record, 'creating');
    },
    [definition.type, activeCompanyContext, onCreateSuccess, onRecordSaved, openCompanyEnableModal],
  );

  const handleUpdateSuccess = useCallback(
    (record: T) => {
      onUpdateSuccess?.(record);
      onRecordSaved?.(record, 'editing');
    },
    [onUpdateSuccess, onRecordSaved],
  );

  // Form mutation hook
  const { submit, isSubmitting } = useMasterForm({
    definition,
    service,
    validate,
    onCreateSuccess: handleCreateSuccess,
    onUpdateSuccess: handleUpdateSuccess,
    toast: toastProp,
    showInternalToasts,
    onFormSave,
    onError,
    t,
  });

  // "Campos modificados" para el DirtyStateDialog — mismo snapshot-diff que
  // MasterCrudForm.tsx:398-410 (getChanges): compara activeRecord (valores
  // originales cargados) contra values (el borrador actual), campo por campo.
  const changedFields = useMemo(() => {
    if (mode !== 'creating' && mode !== 'editing') return [];
    // Creating: the baseline is the auto-seeded defaults (e.g. "Activo" defaulting to
    // true), not an empty record — otherwise every field carrying its own default reads
    // as a user change the instant the create form opens, before anyone touched anything.
    const original = (mode === 'creating' ? initialCreateValues : (activeRecord ?? {})) as Record<string, unknown>;
    const draft = values as Record<string, unknown>;
    const norm = (v: unknown) => (v === undefined || v === null ? '' : v);
    // Fields with a custom `renderCell` (lookups resolving an id to a name, formatted
    // dates, etc.) get resolved through it here too — same function the list column
    // already uses — so the dialog shows "Peso Colombiano → Dólar" instead of the raw
    // guid, matching what the user actually sees for that field everywhere else.
    // `renderCell` needs the row to already carry the joined display column (true for a
    // persisted record fetched from the API, false for a lookup the user just picked in
    // this session — FieldRenderer only puts the raw Id in `draft`). For that case, prefer
    // the `${fieldName}__display` shadow value FieldRenderer stashes alongside the Id.
    const resolve = (raw: unknown, row: Record<string, unknown>, f: ReturnType<typeof getAllFields>[number]) => {
      if (raw !== '' && f.lookupConfig) {
        const shadow = row[`${f.fieldName}__display`];
        if (shadow !== undefined && shadow !== null && shadow !== '') return shadow;
      }
      return raw !== '' && f.renderCell ? f.renderCell(raw, row) : raw;
    };
    const changes: { label: string; from: unknown; to: unknown }[] = [];
    for (const f of getAllFields(definition)) {
      if (!f.showInForm) continue;
      const from = norm(original[f.fieldName]);
      const to = norm(draft[f.fieldName]);
      if (String(from) !== String(to)) {
        changes.push({ label: f.label, from: resolve(from, original, f), to: resolve(to, draft, f) });
      }
    }
    return changes;
  }, [mode, activeRecord, initialCreateValues, values, definition]);

  // `isDirty` (store flag) is set unconditionally on every field change/revert/global-value
  // toggle (MasterPatternViewForm.tsx: handleFieldChange, handleUseGlobalData, handleFieldRevert)
  // and never re-checked against the actual values — so reverting a field back to its original
  // value kept the discard-confirmation dialog showing. `changedFields` is the real field-by-field
  // diff (already used to render the dialog's own content), so it's the correct source of truth
  // for whether there's anything to actually discard.
  const isEffectivelyDirty = changedFields.length > 0 || (externalIsDirty ?? false);

  // The change-status mutation only patches the list cache — when it's triggered from the
  // form's own hamburger menu (record being edited === record being deactivated), the open
  // form keeps showing the stale "Activo" badge and pre-change values unless synced here.
  const handleStatusChangeSuccess = useCallback(
    (record: T) => {
      if (
        activeRecord &&
        (activeRecord as T & { id: string }).id === (record as T & { id: string }).id
      ) {
        updateActiveRecord(record);
        setValues({ ...(record as Record<string, unknown>) });
      }
      onStatusChangeSuccess?.(record);
    },
    [activeRecord, updateActiveRecord, setValues, onStatusChangeSuccess],
  );

  // Change status hook
  const { executeChangeStatus, isChanging } = useChangeStatus({
    service,
    onStatusChangeSuccess: handleStatusChangeSuccess,
    toast: toastProp,
    showInternalToasts,
    t,
  });

  const showLoading = isLoading || externalIsLoading;

  const totalPages = paginationConfig
    ? Math.max(1, Math.ceil(total / searchParams.pageSize))
    : 1;

  // ── Imperative ref API ─────────────────────────────────────────────────────

  const handleOpenCreate = useCallback(() => {
    if (isEffectivelyDirty) {
      openDirtyStateDialog();
    } else {
      openCreate();
    }
  }, [isEffectivelyDirty, openCreate, openDirtyStateDialog]);

  /**
   * Opens a record for editing, then asynchronously fetches the full record via getById
   * so the form shows all fields — not just the subset present in the list row.
   * While the fetch is in-flight, isLoadingRecord blocks form interaction to prevent
   * user edits from being overwritten when the full data arrives.
   * For GLOBAL type in company context, also fetches the global baseline so the
   * "Mostrar datos globales" hint can show the correct value for each overridable field.
   */
  const openEditRecord = useCallback(
    (record: T) => {
      openEdit(record);
      if (!service.getById) return;
      setIsLoadingRecord(true);
      setRecordLoadFailed(false);
      const id = (record as T & { id: string }).id;
      const isGlobalType = definition.type === 'GLOBAL';
      const isCompanyCtx = !!activeCompanyContext && activeCompanyContext !== 'global';
      if (isGlobalType && isCompanyCtx) {
        // Fetch global entity to populate MPV store globalValues (used for built-in field hints).
        service.getById(id).then((globalRecord) => {
          setGlobalValues({ ...(globalRecord as Record<string, unknown>) });
        }).catch(() => {
          // getById failed — globalValues stays as snapshot from openEdit
        });
        // Fetch company-context entity to update activeRecord and values with complete data.
        // Detail tabs read entity._globalValues to determine which fields have overrides
        // and whether to show the RevertToGlobalButton.
        service.getById(id, activeCompanyContext).then((companyRecord) => {
          updateActiveRecord(companyRecord);
          setValues({ ...(companyRecord as Record<string, unknown>) });
        }).catch(() => {
          setRecordLoadFailed(true);
        }).finally(() => {
          setIsLoadingRecord(false);
        });
      } else {
        // For all other types: fetch the full record so all form fields are populated.
        service.getById(id).then((fullRecord) => {
          updateActiveRecord(fullRecord);
          setValues({ ...(fullRecord as Record<string, unknown>) });
        }).catch(() => {
          setRecordLoadFailed(true);
        }).finally(() => {
          setIsLoadingRecord(false);
        });
      }
    },
    [openEdit, definition.type, activeCompanyContext, service, setGlobalValues, updateActiveRecord, setValues, setIsLoadingRecord, setRecordLoadFailed],
  );

  /**
   * Opens a record in view mode, then asynchronously fetches the full record via getById
   * so the form shows all fields — not just the subset present in the list row.
   * While the fetch is in-flight, isLoadingRecord shows a loading overlay on the form.
   */
  const openViewRecord = useCallback(
    (record: T) => {
      openView(record);
      if (!service.getById) return;
      setIsLoadingRecord(true);
      setRecordLoadFailed(false);
      const id = (record as T & { id: string }).id;
      const isCompanyCtx = !!activeCompanyContext && activeCompanyContext !== 'global';
      const companyId = isCompanyCtx ? activeCompanyContext : undefined;
      service.getById(id, companyId).then((fullRecord) => {
        updateActiveRecord(fullRecord);
        setValues({ ...(fullRecord as Record<string, unknown>) });
      }).catch(() => {
        setRecordLoadFailed(true);
      }).finally(() => {
        setIsLoadingRecord(false);
      });
    },
    [openView, activeCompanyContext, service, updateActiveRecord, setValues, setIsLoadingRecord, setRecordLoadFailed],
  );

  // Notifies the host app whenever the form panel opens/closes, regardless of which internal
  // action triggered it (row click, toolbar create, deep-link) — lets the host sync its URL.
  const prevModeRef = useRef(mode);
  useEffect(() => {
    if (prevModeRef.current === mode) return;
    if (mode === 'closed') {
      onRecordClose?.();
    } else {
      onRecordOpen?.(activeRecord as T | null, mode);
    }
    prevModeRef.current = mode;
  }, [mode, activeRecord, onRecordOpen, onRecordClose]);

  // Deep-link: openRecordMode='creating' opens the create form on mount — no id to look up,
  // so this is independent of (and takes priority over) the openRecordId effect below. Guards
  // with a ref (not just `mode !== 'creating'`) so it fires once per 'creating' session instead
  // of re-opening every render, but can fire again if the host navigates away and back.
  const wasAutoCreateModeRef = useRef(false);
  useEffect(() => {
    if (openRecordMode !== 'creating') {
      wasAutoCreateModeRef.current = false;
      return;
    }
    if (wasAutoCreateModeRef.current) return;
    wasAutoCreateModeRef.current = true;
    openCreate();
  }, [openRecordMode, openCreate]);

  // Deep-link: auto-opens the record identified by `openRecordId` (host-controlled, e.g. from
  // its own router/URL). Falls back to service.getById when the record isn't in the loaded list.
  const lastOpenedRecordIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (openRecordMode === 'creating') return;
    if (!openRecordId || openRecordId === lastOpenedRecordIdRef.current) return;
    lastOpenedRecordIdRef.current = openRecordId;
    const record = data.find((r) => (r as T & { id: string }).id === openRecordId);
    const openFn = openRecordMode === 'viewing' ? openViewRecord : openEditRecord;
    if (record) {
      openFn(record as T);
    } else if (service.getById) {
      service.getById(openRecordId).then((loaded) => openFn(loaded)).catch(() => {
        console.warn(`[MasterPatternView] openRecordId "${openRecordId}" not found`);
      });
    }
  }, [openRecordId, openRecordMode, data, service, openEditRecord, openViewRecord]);

  /**
   * Retries the getById fetch for the currently open record.
   * Called from the form's error banner when the initial load failed.
   */
  const handleRetryLoad = useCallback(() => {
    if (!activeRecord || !service.getById) return;
    const id = (activeRecord as T & { id: string }).id;
    setIsLoadingRecord(true);
    setRecordLoadFailed(false);
    const isGlobalType = definition.type === 'GLOBAL';
    const isCompanyCtx = !!activeCompanyContext && activeCompanyContext !== 'global';
    if (isGlobalType && isCompanyCtx) {
      service.getById(id).then((globalRecord) => {
        setGlobalValues({ ...(globalRecord as Record<string, unknown>) });
      }).catch(() => {});
      service.getById(id, activeCompanyContext).then((companyRecord) => {
        updateActiveRecord(companyRecord);
        setValues({ ...(companyRecord as Record<string, unknown>) });
      }).catch(() => {
        setRecordLoadFailed(true);
      }).finally(() => {
        setIsLoadingRecord(false);
      });
    } else {
      service.getById(id).then((fullRecord) => {
        updateActiveRecord(fullRecord);
        setValues({ ...(fullRecord as Record<string, unknown>) });
      }).catch(() => {
        setRecordLoadFailed(true);
      }).finally(() => {
        setIsLoadingRecord(false);
      });
    }
  }, [activeRecord, service, definition.type, activeCompanyContext, setGlobalValues, updateActiveRecord, setValues, setIsLoadingRecord, setRecordLoadFailed]);

  const handleOpenEdit = useCallback(
    async (id: string) => {
      if (isEffectivelyDirty) {
        openDirtyStateDialog();
        return;
      }
      const record = data.find((r) => r.id === id);
      if (record) {
        openEditRecord(record as T);
      } else if (service.getById) {
        try {
          const loaded = await service.getById(id);
          openEditRecord(loaded);
        } catch {
          console.warn(`[MasterPatternView] Record ${id} not found in list and getById failed`);
        }
      } else {
        console.warn(`[MasterPatternView] Record ${id} not found in list and getById not provided`);
      }
    },
    [isEffectivelyDirty, openDirtyStateDialog, data, openEditRecord, service],
  );

  const handleCloseForm = useCallback(() => {
    if (isEffectivelyDirty) {
      openDirtyStateDialog();
    } else {
      closeForm();
    }
  }, [isEffectivelyDirty, openDirtyStateDialog, closeForm]);

  useImperativeHandle(
    ref,
    () => ({
      resetDirty: () => setIsDirty(false),
      refresh: () => {
        queryClient.invalidateQueries({ queryKey: ['masterList'] });
      },
      openCreate: handleOpenCreate,
      openEdit: handleOpenEdit,
      closeForm: handleCloseForm,
    }),
    [setIsDirty, queryClient, handleOpenCreate, handleOpenEdit, handleCloseForm],
  );

  // ── Event handlers ─────────────────────────────────────────────────────────

  const handleCancel = useCallback(() => {
    if (isEffectivelyDirty) {
      openDirtyStateDialog();
    } else {
      closeForm();
    }
  }, [isEffectivelyDirty, openDirtyStateDialog, closeForm]);

  const handleContinueEditing = useCallback(() => {
    closeDirtyStateDialog();
  }, [closeDirtyStateDialog]);

  const handleDiscard = useCallback(() => {
    closeForm();
    closeDirtyStateDialog();
    clearPendingAction();
  }, [closeForm, closeDirtyStateDialog, clearPendingAction]);

  // "Guardar y salir" — mismo patrón que MasterCrud.tsx:2020 (formRef.current.submit()):
  // cierra el diálogo y dispara el submit normal, que ya se encarga de guardar y cerrar
  // el formulario en su propio onSuccess (createMutation/updateMutation en useMasterForm).
  const handleSaveAndExit = useCallback(() => {
    closeDirtyStateDialog();
    submit();
  }, [closeDirtyStateDialog, submit]);

  const handleStatusConfirm = useCallback(
    (id: string, newStatus: boolean) => {
      // Pass companyId when in a company context so the service changes
      // the company-specific status, not the global one
      const companyId =
        activeCompanyContext && activeCompanyContext !== 'global'
          ? activeCompanyContext
          : undefined;
      executeChangeStatus(id, newStatus, companyId);
    },
    [executeChangeStatus, activeCompanyContext],
  );

  // Confirm handler: enables the record for the current company, then proceeds with the original action
  const handleNotEnabledConfirm = useCallback(async () => {
    if (!notEnabledState.record) return;
    const { trigger, record } = notEnabledState;
    // COMPANY_SWITCH uses the pending target company; other triggers use activeCompanyContext
    const targetCompanyId = trigger === 'COMPANY_SWITCH' ? pendingCompanySwitchId : activeCompanyContext;
    if (!targetCompanyId) return;
    const recordId = (record as Record<string, unknown>)['id'] as string;
    setNotEnabledState((prev) => ({ ...prev, isLoading: true }));
    try {
      // For CHANGE_STATUS, pass the target state so the backend creates the assignment
      // with the correct isActive value directly — avoids a second confirmation modal
      const initialValues =
        trigger === 'CHANGE_STATUS'
          ? { isActive: !((record as Record<string, unknown>)['isActive'] as boolean) }
          : undefined;
      await service.companyEnable?.(recordId, targetCompanyId, initialValues);
      queryClient.invalidateQueries({ queryKey: ['masterList'] });
      showSuccess(t('recordEnabledSuccess', 'Registro habilitado correctamente'));
      setNotEnabledState({ isOpen: false, trigger: 'OPEN_EDIT', record: null, isLoading: false });
      if (trigger === 'COMPANY_SWITCH') {
        // Switch context and reload the form with the new company's overrides.
        setActiveCompanyContext(targetCompanyId);
        setPendingCompanySwitchId(null);
        openEdit(record!); // resets store state (overrides, isDirty, revertedFields)
        if (service.getById) {
          setIsLoadingRecord(true);
          setRecordLoadFailed(false);
          // Reload global baseline for override hints.
          service.getById(recordId).then((gr) => {
            setGlobalValues({ ...(gr as Record<string, unknown>) });
          }).catch(() => {});
          // Reload company-specific data (newly enabled → overrides may be empty/defaults).
          service.getById(recordId, targetCompanyId).then((companyRecord) => {
            updateActiveRecord(companyRecord);
            setValues({ ...(companyRecord as Record<string, unknown>) });
          }).catch(() => {
            setRecordLoadFailed(true);
          }).finally(() => {
            setIsLoadingRecord(false);
          });
        }
      } else if (trigger === 'OPEN_EDIT') {
        openEditRecord(record!);
      } else if (trigger === 'OPEN_VIEW') {
        openView(record!);
      }
      // CHANGE_STATUS: no second modal — companyEnable already applies the target state
    } catch (err: unknown) {
      showError((err as Error)?.message ?? t('recordEnabledError', 'Error al habilitar el registro'));
      setNotEnabledState((prev) => ({ ...prev, isLoading: false }));
    }
  }, [notEnabledState, pendingCompanySwitchId, activeCompanyContext, service, queryClient, showSuccess, showError, openEdit, openEditRecord, openView, setActiveCompanyContext, setGlobalValues, setIsLoadingRecord, setRecordLoadFailed, updateActiveRecord, setValues, t]);

  const handleApplyFilters = useCallback(
    (filters: typeof activeFilters) => {
      setActiveFilters(filters);
      // Preserve __view filter if "Mostrar solo compañía" toggle is active
      const withoutView = filters.filter((f) => f.field !== '__view');
      const viewFilter: typeof filters = showOnlyCompany
        ? [{ field: '__view', operator: 'equals', value: 'company' }]
        : [];
      setSearchParams({ page: 1, filters: [...withoutView, ...viewFilter] });
    },
    [setActiveFilters, setSearchParams, showOnlyCompany],
  );

  const handleClearFilters = useCallback(() => {
    // clearFilters resets activeFilters, columnFilters, and quickFilterValues in the store
    clearFilters();
    setShowOnlyCompany(false);
    setSearchParams({ page: 1, filters: [], search: '' });
  }, [clearFilters, setSearchParams]);

  const handleToggleShowOnlyCompany = useCallback(() => {
    const next = !showOnlyCompany;
    setShowOnlyCompany(next);
    const withoutView = searchParams.filters.filter((f) => f.field !== '__view');
    setSearchParams({
      filters: next
        ? [...withoutView, { field: '__view', operator: 'equals', value: 'company' }]
        : withoutView,
      page: 1,
    });
    setActionsDropdownOpen(false);
  }, [showOnlyCompany, searchParams.filters, setSearchParams]);

  const selectedRecords = data.filter((r) => selectedIds.includes(r.id)) as T[];
  const pendingRecord = pendingAction?.payload as T | undefined;

  // Close toolbar dropdown whenever a modal/dialog opens
  const isAnyModalOpen = isChangeStatusModalOpen || isCompanyEnableModalOpen || isDirtyStateDialogOpen;
  React.useEffect(() => {
    if (isAnyModalOpen) setActionsDropdownOpen(false);
  }, [isAnyModalOpen]);

  const isCompanyCtxActive = !!activeCompanyContext && activeCompanyContext !== 'global';
  const showMostrarSoloCompania = definition.type === 'GLOBAL' && isCompanyCtxActive;
  const hasActionsItems = showExport || showImport || (globalActions && globalActions.length > 0) || showMostrarSoloCompania || showViewToggle;

  // Shared by both surfaces that can open the "Habilitar por compañía" panel:
  // the Acciones-column action (global scope, mirrors MasterCrud's 'link' default action)
  // and the dedicated company-status column (company scope, mirrors MasterCrud's
  // showCompanyStatusColumn/CompanyStatusBadge — see ListTable's `onCompanyStatusClick`).
  const handleCompanyEnableClick = useCallback(async (row: T) => {
    const recordId = (row as T & { id: string }).id;
    const prevRecordId = companyEnableRecord
      ? (companyEnableRecord as T & { id: string }).id
      : null;
    setCompanyEnableRecord(row);

    if (service.getAssignedCompanyIds) {
      // Always load from server when available — covers F5 and record switches
      setIsCompanyEnableLoading(true);
      try {
        const ids = await service.getAssignedCompanyIds(recordId);
        setAssignedCompanyIds(ids);
      } finally {
        setIsCompanyEnableLoading(false);
      }
    } else if (recordId !== prevRecordId) {
      // Different record and no server loader — reset so we don't
      // show stale assignments from another record
      setAssignedCompanyIds([]);
    }
    // Same record + no loader → keep optimistic state from last confirm

    openCompanyEnableModal();
  }, [companyEnableRecord, service, setCompanyEnableRecord, setIsCompanyEnableLoading, setAssignedCompanyIds, openCompanyEnableModal]);

  // Row-level actions injected by MasterPatternView itself (not passed by the consumer):
  // "Habilitar compañías" (GLOBAL type — shown in both scopes; in company scope it sits
  // alongside the dedicated company-status column, see `onCompanyStatusClick` below) and
  // "Auditoría de Cambios" (opt-in via the `auditHistory` prop) — mirrors MasterCrud's
  // 'link' and 'audit' default actions.
  const injectedRowActions = [
    ...(definition.type === 'GLOBAL' ? [{
      key: 'company-enable',
      label: t('enableCompaniesAction', 'Habilitar compañías'),
      icon: <Building2 size={18} />,
      position: 'row' as const,
      onExecute: async (row?: T) => {
        if (!row) return;
        await handleCompanyEnableClick(row);
      },
    }] : []),
    ...(auditHistory?.enabled ? [{
      key: 'audit',
      label: t('auditChanges', 'Auditoría de Cambios'),
      icon: <ClockIcon className="w-[15px] h-[15px]" />,
      position: 'row' as const,
      onExecute: (row?: T) => {
        if (row) openAuditModal(row);
      },
    }] : []),
  ];

  return (
    <div data-testid="master-pattern-view" className="relative flex flex-col w-full h-screen overflow-hidden">
      {/* Header, company selector and list are hidden while navigationType='page' has a record
          open — PagePanel fully replaces them (and is transparent), instead of painting over
          them like sidebar/modal do. Keeps this content out of the DOM entirely. */}
      {(navigationType !== 'page' || mode === 'closed') && (
      <>
      {/* Header: (Title + CompanySelector) + Búsqueda/Acciones/Crear — apila en columna en
          pantallas angostas (igual que MasterCrudToolbar.tsx:101-114): título y selector de
          compañía viven juntos en la columna izquierda, así en angosto quedan ARRIBA de los
          botones de búsqueda/crear, no debajo — antes el selector era una fila aparte que
          siempre caía después de los botones. */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 px-5 pt-3.5 pb-2">
        <div className="flex flex-col items-start gap-1.5 min-w-0">
          {title && (
            <h1 className="text-xl font-semibold text-[#1355b7] tracking-tight truncate" data-testid="master-view-title">
              {title}
            </h1>
          )}
          {definition.type !== 'UNIVERSAL' && (
            <CompanySelector
              companies={companies ?? []}
              activeCompanyId={activeCompanyContext ?? undefined}
              showGlobalOption={definition.type === 'GLOBAL'}
              onCompanyChange={(company) => {
                setActiveCompanyContext(company.id);
                onCompanyChange?.(company);
              }}
              toast={toastProp}
              showInternalToasts={showInternalToasts}
              t={t}
            />
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap md:flex-nowrap">
          {/* Búsqueda rápida — input de texto real (MasterCrudToolbar.tsx:118-131);
              el ícono de filtro es un botón aparte que abre el FilterPanel. Ambos son
              mutuamente excluyentes: el input se deshabilita mientras el panel está
              abierto (igual que `disabled={isAdvancedSearchActive}` en MasterCrud). */}
          <div className="relative flex items-center">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-tertiary dark:text-dark-content-tertiary pointer-events-none">
              <SearchIcon className="w-3.5 h-3.5" />
            </span>
            <input
              type="search"
              data-testid="toolbar-busqueda-input"
              placeholder={t('toolbarSearchLabel', 'Buscar...')}
              value={searchParams.search}
              autoComplete="off"
              disabled={isFilterPanelOpen}
              onChange={(e) => setSearchParams({ search: e.target.value, page: 1 })}
              className="pl-8 pr-10 h-9 w-64 text-sm border border-border-primary dark:border-dark-border-primary rounded-md bg-white dark:bg-dark-bg-primary text-content-primary dark:text-dark-content-primary placeholder:text-content-tertiary dark:placeholder:text-dark-content-tertiary focus:outline-none focus:border-[#93d1fd] focus:ring-[3px] focus:ring-primary-500/10 transition-colors disabled:opacity-60"
            />
            <motion.button
              type="button"
              onClick={toggleFilterPanel}
              aria-expanded={isFilterPanelOpen}
              title={isFilterPanelOpen ? t('hideAdvancedSearch', 'Ocultar filtros') : t('showAdvancedSearch', 'Mostrar filtros')}
              data-testid="toolbar-busqueda-button"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.92 }}
              transition={{ type: 'spring', bounce: 0.55, duration: 0.4 }}
              className={`absolute right-1.5 inline-flex items-center justify-center w-7 h-7 rounded-md transition-colors ${
                isFilterPanelOpen || activeFilters.length > 0
                  ? 'bg-primary-600 text-white'
                  : 'text-content-tertiary hover:text-primary-600 hover:bg-[#eff8ff] dark:hover:bg-primary-900/20'
              }`}
            >
              {isFilterPanelOpen ? <ChevronDownIcon className="w-3.5 h-3.5 rotate-180" /> : <FilterIcon className="w-3.5 h-3.5" />}
            </motion.button>
          </div>

          <ToolbarActions<T>
            definition={definition}
            permissions={permissions}
            defaultActions={defaultActions}
            onCreateClick={handleOpenCreate}
            toast={toastProp}
            showInternalToasts={showInternalToasts}
            t={t}
          />

          {/* Acciones dropdown — always visible, kebab vertical estilo MasterCrud */}
          <div className="relative">
            <motion.button
              type="button"
              onClick={() => hasActionsItems && setActionsDropdownOpen((v) => !v)}
              data-testid="toolbar-actions-dropdown-button"
              aria-haspopup="menu"
              aria-expanded={actionsDropdownOpen}
              title={t('moreOptions', 'Más opciones')}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.92 }}
              transition={{ type: 'spring', bounce: 0.55, duration: 0.4 }}
              className="inline-flex items-center justify-center w-9 h-9 rounded-md border bg-background-secondary dark:bg-dark-bg-secondary border-border-primary dark:border-dark-border-primary text-content-tertiary dark:text-dark-content-tertiary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary transition-colors"
            >
              <EllipsisVerticalIcon className="w-4 h-4" />
            </motion.button>
            {actionsDropdownOpen && hasActionsItems && (
              <div className="absolute right-0 top-full mt-1 bg-bg-primary dark:bg-dark-bg-primary rounded-md border border-border-primary dark:border-dark-border-primary shadow-md z-50 w-64 py-1">
                {/* Ver solo registros de la compañía seleccionada — misma etiqueta y mismo
                    componente Switch que el toggle de alcance de filas de MasterCrud
                    (MasterCrudToolbar.tsx:191-212, prop viewScope). */}
                {showMostrarSoloCompania && (
                  <div className="px-1 py-1 border-b border-border-primary dark:border-dark-border-primary">
                    <p className="px-2 pt-1 pb-1.5 text-[11px] font-semibold text-content-tertiary dark:text-dark-content-tertiary tracking-[0.3px]">
                      {t('recordsToShow', 'Registros a mostrar')}
                    </p>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={showOnlyCompany}
                      onClick={handleToggleShowOnlyCompany}
                      data-testid="toolbar-toggle-only-company"
                      className="w-full flex items-center justify-between gap-3 px-2 py-2 rounded text-sm text-content-primary dark:text-dark-content-primary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary transition-colors"
                    >
                      <span className="flex-1 text-left select-none">
                        {t('viewCompanyOnly', 'Ver solo registros de la compañía seleccionada')}
                      </span>
                      <Switch
                        size="sm"
                        checked={showOnlyCompany}
                        onChange={() => {}}
                        className="pointer-events-none shrink-0"
                      />
                    </button>
                  </div>
                )}
                {/* "Mostrar datos globales" NO va aquí — a diferencia de MasterCrud (que no tiene
                    este concepto en su lista), en MPV solo tiene sentido dentro del formulario de
                    un registro (dónde ver hints de campo sobreescrito vs global). Sigue disponible
                    ahí mismo, en el menú hamburguesa del form (FormActionBar.tsx:305). */}
                {/* Modo de visualización (Listado / Tarjetas) — dentro del kebab, como MasterCrud.
                    El separador con la sección anterior/siguiente es el border-b de este propio
                    div (como en MasterCrudToolbar.tsx:216) — no un <hr> aparte, que duplicaba la línea. */}
                {showViewToggle && (
                  <div className={`px-3 py-2 ${(showImport || showExport) ? 'border-b border-border-primary dark:border-dark-border-primary' : ''}`}>
                    <p className="text-[11px] font-semibold text-content-tertiary dark:text-dark-content-tertiary tracking-[0.3px] mb-2">
                      {t('displayMode', 'Modo de visualización')}
                    </p>
                    <div className="inline-flex w-full border border-border-primary dark:border-dark-border-primary rounded-md overflow-hidden text-sm">
                      <button
                        type="button"
                        onClick={() => { setViewMode('table'); setActionsDropdownOpen(false); }}
                        aria-pressed={viewMode === 'table'}
                        data-testid="toolbar-view-table"
                        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 transition-colors ${
                          viewMode === 'table'
                            ? 'bg-primary-600 text-white'
                            : 'bg-white dark:bg-dark-bg-primary text-content-tertiary dark:text-dark-content-tertiary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary'
                        }`}
                      >
                        <ListBulletIcon className="w-4 h-4" />
                        <span>{t('listView', 'Listado')}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => { setViewMode('cards'); setActionsDropdownOpen(false); }}
                        aria-pressed={viewMode === 'cards'}
                        data-testid="toolbar-view-cards"
                        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 transition-colors border-l border-border-primary dark:border-dark-border-primary ${
                          viewMode === 'cards'
                            ? 'bg-primary-600 text-white'
                            : 'bg-white dark:bg-dark-bg-primary text-content-tertiary dark:text-dark-content-tertiary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary'
                        }`}
                      >
                        <Squares2X2Icon className="w-4 h-4" />
                        <span>{t('cardView', 'Tarjetas')}</span>
                      </button>
                    </div>
                  </div>
                )}
                {showImport && (
                  <button type="button" className="w-full flex items-center gap-2 px-3 py-2 text-sm text-content-primary dark:text-dark-content-primary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary cursor-pointer" onClick={() => { onImport?.(); setActionsDropdownOpen(false); }} data-testid="toolbar-import-item">
                    <ArrowUpTrayIcon className="w-[13px] h-[13px]" /> {t('import', 'Importar')}
                  </button>
                )}
                {showExport && (
                  <button type="button" className="w-full flex items-center gap-2 px-3 py-2 text-sm text-content-primary dark:text-dark-content-primary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary cursor-pointer" onClick={() => { onExport?.(); setActionsDropdownOpen(false); }} data-testid="toolbar-export-item">
                    <DocumentArrowDownIcon className="w-[13px] h-[13px]" /> {t('export', 'Exportar')}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content area: filter-card (conditional) + grid-card.
          overflow-y-auto AQUÍ (no en el header): el header/buscador vive fuera de este
          contenedor como hermano, así que nunca se pierde de vista al hacer scroll — sin
          necesitar `sticky`. Es el ÚNICO scroll de la vista (ni el panel ni la grilla tienen
          overflow propio) para no anidar scrollbars. */}
      <div className="flex-1 min-h-0 flex flex-col gap-2.5 px-5 pb-5 pt-3 overflow-y-auto">
        {/* Filter card — only visible when Búsqueda is active.
            Se mantiene SIEMPRE montado (se oculta con `hidden`, no con `&&`) para que
            el estado local de FilterPanel (valores tecleados, condiciones avanzadas,
            conector Y/O) sobreviva a cerrar/abrir el panel — incluso lo que el usuario
            escribió pero nunca llegó a buscar. Desmontarlo lo reiniciaba cada vez.
            Sin max-h/overflow propio: se adapta a su alto natural: si crece mucho, empuja
            la grilla hacia abajo y el scroll del content-area (arriba) se encarga. */}
        <div
          className={`bg-[#f8f9fc] dark:bg-zinc-900 rounded-lg border border-border-primary dark:border-dark-border-primary shadow-sm flex-shrink-0 ${
            isFilterPanelOpen ? '' : 'hidden'
          }`}
        >
          <FilterPanel
            definition={definition}
            activeFilters={activeFilters}
            onSearch={handleApplyFilters}
            onClear={handleClearFilters}
            t={t}
          />
        </div>

        {/* Grid card: table + empty state + pagination + batch bar.
            min-h SOLO cuando el filtro está abierto (isFilterPanelOpen): con el filtro
            cerrado vuelve a min-h-0 puro — el tamaño de siempre, sin scroll de más. Con el
            filtro abierto, la grilla no se encoge por debajo de ese piso: el filtro la
            empuja hacia abajo (dentro del scroll del content-area) en vez de aplastarla. */}
        <div className={`${viewMode === 'cards' ? 'bg-[#f8f9fc]' : 'bg-background-secondary'} dark:bg-dark-bg-secondary rounded-xl border border-border-primary dark:border-dark-border-primary overflow-hidden flex flex-col flex-1 ${isFilterPanelOpen ? 'min-h-[26rem]' : 'min-h-0'}`}>
          <div ref={scrollContainerRef} className="flex-1 min-h-0 overflow-auto flex flex-col">
            {showLoading || hasSearched ? (
              viewMode === 'cards' ? (
                <ListCard<T>
                  definition={definition}
                  data={data as T[]}
                  onOpenView={(row) => {
                    const isCompanyCtx = !!activeCompanyContext && activeCompanyContext !== 'global';
                    if (definition.type === 'GLOBAL' && isCompanyCtx && !(row as Record<string, unknown>)['isAssigned']) {
                      setNotEnabledState({ isOpen: true, trigger: 'OPEN_VIEW', record: row, isLoading: false });
                      return;
                    }
                    openViewRecord(row);
                  }}
                  onOpenEdit={(row) => {
                    if (isEffectivelyDirty) { openDirtyStateDialog(); return; }
                    const isCompanyCtx = !!activeCompanyContext && activeCompanyContext !== 'global';
                    if (definition.type === 'GLOBAL' && isCompanyCtx && !(row as Record<string, unknown>)['isAssigned']) {
                      setNotEnabledState({ isOpen: true, trigger: 'OPEN_EDIT', record: row, isLoading: false });
                      return;
                    }
                    openEditRecord(row);
                  }}
                  onChangeStatus={(row) => {
                    const isCompanyCtx = !!activeCompanyContext && activeCompanyContext !== 'global';
                    if (definition.type === 'GLOBAL' && isCompanyCtx && !(row as Record<string, unknown>)['isAssigned']) {
                      setNotEnabledState({ isOpen: true, trigger: 'CHANGE_STATUS', record: row, isLoading: false });
                      return;
                    }
                    setPendingAction({ type: 'change-status', payload: row });
                    openChangeStatusModal();
                  }}
                  isLoading={showLoading && !(paginationConfig?.mode === 'infinite' && data.length > 0)}
                  modalOpen={isAnyModalOpen || notEnabledState.isOpen}
                  activeCompanyContext={activeCompanyContext ?? undefined}
                  permissions={permissions}
                  actions={injectedRowActions}
                  onCompanyStatusClick={definition.type === 'GLOBAL' ? handleCompanyEnableClick : undefined}
                  renderCardContent={renderCardContent}
                  toast={toastProp}
                  showInternalToasts={showInternalToasts}
                  defaultActions={defaultActions}
                  t={t}
                />
              ) : (
                <ListTable<T>
                  definition={definition}
                  data={data as T[]}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={setSort}
                  onOpenView={(row) => {
                    const isCompanyCtx = !!activeCompanyContext && activeCompanyContext !== 'global';
                    if (definition.type === 'GLOBAL' && isCompanyCtx && !(row as Record<string, unknown>)['isAssigned']) {
                      setNotEnabledState({ isOpen: true, trigger: 'OPEN_VIEW', record: row, isLoading: false });
                      return;
                    }
                    openViewRecord(row);
                  }}
                  onOpenEdit={(row) => {
                    if (isEffectivelyDirty) { openDirtyStateDialog(); return; }
                    const isCompanyCtx = !!activeCompanyContext && activeCompanyContext !== 'global';
                    if (definition.type === 'GLOBAL' && isCompanyCtx && !(row as Record<string, unknown>)['isAssigned']) {
                      setNotEnabledState({ isOpen: true, trigger: 'OPEN_EDIT', record: row, isLoading: false });
                      return;
                    }
                    openEditRecord(row);
                  }}
                  onChangeStatus={(row) => {
                    const isCompanyCtx = !!activeCompanyContext && activeCompanyContext !== 'global';
                    if (definition.type === 'GLOBAL' && isCompanyCtx && !(row as Record<string, unknown>)['isAssigned']) {
                      setNotEnabledState({ isOpen: true, trigger: 'CHANGE_STATUS', record: row, isLoading: false });
                      return;
                    }
                    setPendingAction({ type: 'change-status', payload: row });
                    openChangeStatusModal();
                  }}
                  isLoading={showLoading && !(paginationConfig?.mode === 'infinite' && data.length > 0)}
                  modalOpen={isAnyModalOpen || notEnabledState.isOpen}
                  activeCompanyContext={activeCompanyContext ?? undefined}
                  permissions={permissions}
                  actions={injectedRowActions}
                  onCompanyStatusClick={definition.type === 'GLOBAL' ? handleCompanyEnableClick : undefined}
                  toast={toastProp}
                  showInternalToasts={showInternalToasts}
                  defaultActions={defaultActions}
                  t={t}
                />
              )
            ) : (
              <EmptyState hasSearched={hasSearched} t={t} />
            )}

            {/* Filler: when few records don't fill the viewport, absorbs remaining space using
                the outer bg — no stark gap against the card border */}
            {hasSearched && data.length > 0 && !showLoading && (
              <div className={`flex-1 ${viewMode === 'cards' ? 'bg-[#f8f9fc]' : 'bg-bg-primary'} dark:bg-dark-bg-primary`} />
            )}

            {/* Infinite scroll — loading indicator + bottom padding for scroll trigger */}
            {paginationConfig?.mode === 'infinite' && (
              <>
                {isFetchingNextPage && (
                  <div className="flex justify-center py-3 text-sm text-content-tertiary dark:text-dark-content-tertiary">
                    {t('loadingMore', 'Cargando más...')}
                  </div>
                )}
              </>
            )}
          </div>

          {paginationConfig?.mode !== 'infinite' && paginationConfig && data.length > 0 && !showLoading && (
            <PaginationBar
              currentPage={searchParams.page}
              totalPages={totalPages}
              totalRecords={total}
              pageSize={searchParams.pageSize}
              pageSizeOptions={paginationConfig.pageSizeOptions ?? [10, 25, 50]}
              onPageChange={(page) => setSearchParams({ page })}
              onPageSizeChange={(pageSize) => setSearchParams({ pageSize, page: 1 })}
              t={t}
            />
          )}

          {selectable && (
            <BatchBar<T>
              selectedIds={selectedIds}
              selectedRecords={selectedRecords}
              onClearSelection={clearSelection}
              permissions={permissions ?? {}}
              batchActions={batchActions}
              toast={toastProp}
              showInternalToasts={showInternalToasts}
              t={t}
            />
          )}
        </div>
      </div>
      </>
      )}

      <MasterPatternViewForm<T>
        definition={definition}
        entityName={entityName}
        permissions={permissions}
        navigationType={navigationType}
        showBackButton={showBackButton}
        formWidth={formWidth}
        formHeight={formHeight}
        tabs={tabs}
        headerFields={headerFields}
        renderForm={renderForm}
        detailTabs={detailTabs}
        onSave={submit}
        onCancel={handleCancel}
        onRetryLoad={handleRetryLoad}
        onRequestRefresh={() => {
          queryClient.invalidateQueries({ queryKey: ['masterList'] });
        }}
        companies={companies}
        assignedCompanyIds={formCompanyIds}
        onHamburgerEnableCompany={async () => {
          if (!activeRecord) return;
          const recordId = (activeRecord as T & { id: string }).id;
          const prevRecordId = companyEnableRecord
            ? (companyEnableRecord as T & { id: string }).id
            : null;
          setCompanyEnableRecord(activeRecord as T);
          if (service.getAssignedCompanyIds) {
            setIsCompanyEnableLoading(true);
            try {
              const ids = await service.getAssignedCompanyIds(recordId);
              setAssignedCompanyIds(ids);
            } finally {
              setIsCompanyEnableLoading(false);
            }
          } else if (recordId !== prevRecordId) {
            setAssignedCompanyIds([]);
          }
          openCompanyEnableModal();
        }}
        onHamburgerAudit={auditHistory?.enabled ? () => {
          if (activeRecord) openAuditModal(activeRecord as T);
        } : undefined}
        onCompanyChange={async (company) => {
          // While editing a GLOBAL record, a company switch requires special handling:
          // guard dirty state, check assignment, then reload form with the new company's data.
          if (
            definition.type === 'GLOBAL' &&
            mode === 'editing' &&
            activeRecord
          ) {
            // Block switch when there are unsaved changes — avoids cross-company data corruption.
            if (isEffectivelyDirty) {
              showError(t('unsavedChangesBlockSwitch', 'Tienes cambios sin guardar. Guarda o cancela los cambios antes de cambiar de compañía.'));
              return;
            }

            const recordId = (activeRecord as T & { id: string }).id;

            // Switching to 'global' context: reload form with the global baseline.
            if (company.id === 'global') {
              setActiveCompanyContext('global');
              onCompanyChange?.(company);
              if (service.getById) {
                setIsLoadingRecord(true);
                setRecordLoadFailed(false);
                service.getById(recordId).then((globalRecord) => {
                  updateActiveRecord(globalRecord);
                  setValues({ ...(globalRecord as Record<string, unknown>) });
                  setGlobalValues({});
                }).catch(() => {
                  setRecordLoadFailed(true);
                }).finally(() => {
                  setIsLoadingRecord(false);
                });
              }
              return;
            }

            // Switching to a specific company: check if the record is assigned.
            let isAssigned = false;
            if (service.getAssignedCompanyIds) {
              try {
                const ids = await service.getAssignedCompanyIds(recordId);
                isAssigned = ids.includes(company.id);
              } catch {
                isAssigned = false;
              }
            } else if (
              companyEnableRecord &&
              (companyEnableRecord as T & { id: string }).id === recordId
            ) {
              isAssigned = assignedCompanyIds.includes(company.id);
            }

            // Not assigned → offer to enable it inline instead of just blocking the switch
            // (regresó en 3a8a918, que lo cambió por un toast de error sin ofrecer habilitar).
            if (!isAssigned) {
              setPendingCompanySwitchId(company.id);
              setNotEnabledState({
                isOpen: true,
                trigger: 'COMPANY_SWITCH',
                record: activeRecord as T,
                isLoading: false,
              });
              return;
            }

            // Assigned → switch context and reload form with the new company's overrides.
            setActiveCompanyContext(company.id);
            onCompanyChange?.(company);
            if (service.getById) {
              setIsLoadingRecord(true);
              setRecordLoadFailed(false);
              service.getById(recordId).then((globalRecord) => {
                setGlobalValues({ ...(globalRecord as Record<string, unknown>) });
              }).catch(() => {});
              service.getById(recordId, company.id).then((companyRecord) => {
                updateActiveRecord(companyRecord);
                setValues({ ...(companyRecord as Record<string, unknown>) });
              }).catch(() => {
                setRecordLoadFailed(true);
              }).finally(() => {
                setIsLoadingRecord(false);
              });
            }
            return;
          }

          // Non-edit mode or non-GLOBAL type: normal switch.
          setActiveCompanyContext(company.id);
          onCompanyChange?.(company);
        }}
        recordTitleFields={recordTitleFields}
        companyDisabled={isDirty || (definition.type === 'COMPANY_SPECIFIC' && mode !== 'closed')}
        canEdit={canEdit}
      />

      <DirtyStateDialog
        isOpen={isDirtyStateDialogOpen}
        onContinue={handleContinueEditing}
        onDiscard={handleDiscard}
        onSaveAndExit={handleSaveAndExit}
        changes={changedFields}
        t={t}
      />

      <ChangeStatusModal<T>
        isOpen={isChangeStatusModalOpen}
        record={pendingRecord ?? null}
        onCancel={closeChangeStatusModal}
        onConfirm={handleStatusConfirm}
        isLoading={isChanging}
        t={t}
      />

      {/* Company Enable Modal — GLOBAL type only */}
      {definition.type === 'GLOBAL' && (
        <CompanyEnableModal
          isOpen={isCompanyEnableModalOpen}
          recordId={companyEnableRecord ? (companyEnableRecord as T & { id: string }).id : ''}
          recordLabel={
            companyEnableRecord
              ? String(
                  (companyEnableRecord as Record<string, unknown>).code ??
                    (companyEnableRecord as Record<string, unknown>).name ??
                    '',
                ) || undefined
              : undefined
          }
          companies={companies ?? []}
          assignedCompanyIds={assignedCompanyIds}
          t={t}
          onClose={() => {
            const wasPostCreate = isPostCreateModalRef.current;
            isPostCreateModalRef.current = false;
            closeCompanyEnableModal();
            if (wasPostCreate) closeForm();
          }}
          onConfirm={async (changes) => {
            if (!companyEnableRecord) return;
            const recordId = (companyEnableRecord as T & { id: string }).id;
            setIsCompanyEnableLoading(true);
            try {
              for (const change of changes) {
                if (change.action === 'enable') {
                  await service.companyEnable?.(recordId, change.companyId);
                } else {
                  await service.uncompanyEnable?.(recordId, change.companyId);
                }
              }
              // Update local assigned state so next modal open reflects changes
              setAssignedCompanyIds((prev) => {
                const next = new Set(prev);
                for (const change of changes) {
                  if (change.action === 'enable') next.add(change.companyId);
                  else next.delete(change.companyId);
                }
                return [...next];
              });
              showSuccess(t('companyChangesSavedSuccess', 'Cambios de compañías guardados correctamente'));
              queryClient.invalidateQueries({ queryKey: ['masterList'] });
              const wasPostCreate = isPostCreateModalRef.current;
              isPostCreateModalRef.current = false;
              closeCompanyEnableModal();
              if (wasPostCreate) closeForm();
            } catch (err: any) {
              showError(err?.message ?? t('companyChangesSavedError', 'Error al guardar los cambios de compañías'));
            } finally {
              setIsCompanyEnableLoading(false);
            }
          }}
          isLoading={isCompanyEnableLoading}
        />
      )}

      {/* Not-Enabled Modal — GLOBAL type: record not yet assigned to the active company */}
      {definition.type === 'GLOBAL' && (
        <NotEnabledModal
          isOpen={notEnabledState.isOpen}
          trigger={notEnabledState.trigger}
          companyName={(() => {
            // COMPANY_SWITCH: activeCompanyContext todavía es el contexto viejo — el modal
            // pregunta por la compañía DESTINO (pendingCompanySwitchId), no la actual.
            const targetId = notEnabledState.trigger === 'COMPANY_SWITCH'
              ? pendingCompanySwitchId
              : activeCompanyContext;
            return companies?.find((c) => c.id === targetId)?.name ?? targetId ?? '';
          })()}
          onCancel={closeNotEnabled}
          onConfirm={handleNotEnabledConfirm}
          isLoading={notEnabledState.isLoading}
          t={t}
        />
      )}

      {/* Audit History Modal — opt-in via the `auditHistory` prop, mirrors MasterCrud.tsx:2112-2131 */}
      {auditModalState.show && auditHistory && auditModalState.record && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-dark-bg-primary rounded-xl shadow-2xl flex flex-col relative w-full max-w-3xl max-h-[90vh]">
            <div className="flex items-center justify-between p-4 border-b border-border-secondary dark:border-dark-border-primary">
              <h3 className="text-lg font-bold text-content-primary dark:text-dark-content-primary">
                {t('auditHistory', 'Historial de cambios')}
              </h3>
              <Button
                type="outline"
                size="xs"
                iconOnly
                leftIcon={<XMarkIcon className="w-4 h-4" />}
                onClick={closeAuditModal}
                ariaLabel={t('close', 'Cerrar')}
                className="!bg-white dark:!bg-dark-bg-primary"
              />
            </div>
            <div className="flex-1 overflow-hidden">
              <AuditHistoryPanel
                fetchFn={auditHistory.fetchFn}
                entityId={String((auditModalState.record as T & { id: string }).id ?? '')}
              />
            </div>
          </div>
        </div>
      )}

      {showInternalToasts && <InternalToastContainer />}

      <ConflictDialog
        t={t}
        onRefresh={async () => {
          queryClient.invalidateQueries({ queryKey: ['masterList'] });
          const recordId = (activeRecord as T & { id: string })?.id;
          if (recordId && service.getById) {
            const ctxCompanyId =
              activeCompanyContext && activeCompanyContext !== 'global'
                ? activeCompanyContext
                : undefined;
            const record = await service.getById(recordId, ctxCompanyId);
            openEditRecord(record);
          }
          clearPendingAction();
        }}
      />
    </div>
  );
}) as <T extends MasterPatternViewEntity>(
  props: ContentProps<T> & { ref?: React.ForwardedRef<MasterPatternViewRef> }
) => React.ReactElement | null;

// ─── CustomContent — escape hatch for CUSTOM type ────────────────────────────

interface CustomContentProps {
  definition: MasterPatternViewDefinition;
  renderBody?: (ctx: import('../types/props.types').CustomBodyContext) => React.ReactNode;
  title: string;
  companies?: import('../types/service.types').MasterPatternViewCompany[];
  defaultCompany?: import('../types/service.types').MasterPatternViewCompany;
  onCompanyChange?: (company: import('../types/service.types').MasterPatternViewCompany) => void;
  toast?: (type: 'success' | 'error' | 'warning' | 'info', message: string) => void;
  showCompanySelector?: boolean;
  customSave?: import('../types/props.types').CustomSaveConfig;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderActionsMenuItems?: (ctx: any) => React.ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderToolbarActions?: (ctx: any) => React.ReactNode;
  t?: (key: string, defaultValue?: string) => string;
}

function CustomContent({
  definition,
  renderBody,
  title,
  companies = [],
  defaultCompany,
  onCompanyChange,
  showCompanySelector,
  customSave,
  renderActionsMenuItems,
  renderToolbarActions,
  t = (key: string, defaultValue?: string) => defaultValue ?? key,
}: CustomContentProps) {
  const companyBehavior = definition.companyBehavior ?? 'NONE';
  const activeCompanyContext = useMasterViewStore((s) => s.activeCompanyContext);
  const setActiveCompanyContext = useMasterViewStore((s) => s.setActiveCompanyContext);

  const [isSaving, setIsSaving] = useState(false);
  const [actionsDropdownOpen, setActionsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (companyBehavior === 'COMPANY_SPECIFIC' && companies.length > 0) {
      const initial = defaultCompany ?? companies[0];
      setActiveCompanyContext(initial.id);
      // Mismo aviso que en el efecto de restauración de MasterPatternView: el consumidor
      // debe enterarse de la compañía inicial igual que se entera de un cambio manual
      // (ver ctx.setCompanyId más abajo), o su estado de "compañía seleccionada" queda
      // desincronizado del store interno de MPV.
      if (initial.id !== 'global') onCompanyChange?.(initial);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    if (!actionsDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setActionsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [actionsDropdownOpen]);

  const companyId = activeCompanyContext === 'global' ? undefined : (activeCompanyContext ?? undefined);
  const isCompanyMode = companyBehavior !== 'NONE' && !!activeCompanyContext && activeCompanyContext !== 'global';

  const handleCustomSave = useCallback(async () => {
    if (!customSave) return;
    const { onBeforeSave, onSave, onAfterSave } = customSave;
    if (onBeforeSave) {
      const canProceed = await onBeforeSave();
      if (canProceed === false) return;
    }
    setIsSaving(true);
    try {
      await onSave();
      await onAfterSave?.();
    } finally {
      setIsSaving(false);
    }
  }, [customSave]);

  const ctx: import('../types/props.types').CustomBodyContext = {
    companies,
    companyId,
    isCompanyMode,
    isSaving,
    setCompanyId: (id) => {
      setActiveCompanyContext(id ?? '');
      if (id && id !== 'global') {
        const company = companies.find((c) => c.id === id);
        if (company) onCompanyChange?.(company);
      }
    },
  };

  // Determine visibility of toolbar and company selector
  const hasActionsItems = !!renderActionsMenuItems;
  const hasSaveButton = !!customSave && !customSave.hidden;
  const hasToolbar = hasActionsItems || !!renderToolbarActions || hasSaveButton;
  const displayCompanySelector = showCompanySelector ?? (companyBehavior !== 'NONE');

  return (
    <div data-testid="master-pattern-view" className="relative flex flex-col h-screen overflow-hidden">
      {/* Header: Title + Acciones + renderToolbarActions + Save */}
      <div className="flex items-center justify-between px-5 pt-3.5 pb-0">
        {title && (
          <h1 className="text-xl font-semibold text-[#1355b7] tracking-tight" data-testid="master-view-title">
            {title}
          </h1>
        )}

        {hasToolbar && (
          <div className="ml-auto flex items-center gap-2">
            {/* Acciones dropdown */}
            {hasActionsItems && (
              <div className="relative" ref={dropdownRef}>
                <motion.button
                  type="button"
                  onClick={() => setActionsDropdownOpen((v) => !v)}
                  data-testid="custom-toolbar-actions-dropdown-button"
                  aria-haspopup="menu"
                  aria-expanded={actionsDropdownOpen}
                  title={t('moreOptions', 'Más opciones')}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.92 }}
                  transition={{ type: 'spring', bounce: 0.55, duration: 0.4 }}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-md border bg-background-secondary dark:bg-dark-bg-secondary border-border-primary dark:border-dark-border-primary text-content-tertiary dark:text-dark-content-tertiary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary transition-colors"
                >
                  <EllipsisVerticalIcon className="w-4 h-4" />
                </motion.button>

                {actionsDropdownOpen && (
                  <div className="absolute right-0 top-full mt-1 bg-bg-primary dark:bg-dark-bg-primary rounded-md border border-border-primary dark:border-dark-border-primary shadow-md z-50 w-64 py-1">
                    {renderActionsMenuItems(ctx)}
                  </div>
                )}
              </div>
            )}

            {/* Custom toolbar buttons (right of Acciones) */}
            {renderToolbarActions?.(ctx)}

            {/* Declarative save button */}
            {hasSaveButton && (
              <button
                type="button"
                onClick={() => void handleCustomSave()}
                disabled={customSave!.disabled || isSaving}
                data-testid="custom-save-button"
                className="inline-flex items-center gap-2 px-3 h-9 rounded-md text-sm font-bold cursor-pointer transition-all bg-[#0e79fd] text-white hover:bg-[#0c6be0] disabled:opacity-50"
              >
                {isSaving
                  ? (customSave!.savingLabel ?? t('saving', 'Guardando…'))
                  : (customSave!.label ?? t('save', 'Guardar'))}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Company selector — shown based on showCompanySelector (default: true when companyBehavior !== 'NONE') */}
      {displayCompanySelector && (
        <div className="px-5 pb-2 pt-2">
          <CompanySelector
            companies={companies}
            activeCompanyId={activeCompanyContext ?? undefined}
            showGlobalOption={companyBehavior === 'GLOBAL'}
            onCompanyChange={(company) => ctx.setCompanyId(company.id)}
          />
        </div>
      )}

      <div className="flex-1 min-h-0 flex flex-col gap-2.5 px-5 pb-5 pt-3">
        {renderBody?.(ctx)}
      </div>
    </div>
  );
}

/**
 * MasterPatternView — complete master data management component.
 *
 * @example
 * ```tsx
 * const ref = useRef<MasterPatternViewRef>(null);
 *
 * <MasterPatternView
 *   ref={ref}
 *   definition={currencyDefinition}
 *   service={currencyService}
 *   title="Monedas"
 *   entityName="Moneda"
 * />
 * ```
 */
export const MasterPatternView = forwardRef(function MasterPatternViewWrapper<
  T extends MasterPatternViewEntity,
>(
  props: MasterPatternViewProps<T>,
  ref: React.ForwardedRef<MasterPatternViewRef>,
) {
  // When cacheKey is provided, create an isolated QueryClient per key value.
  // Prevents cache contamination when two MPV instances share the same entityName
  // (e.g., a child entity list nested inside a parent MPV's detailTab).
  // When omitted, falls back to the shared module-level client — zero behavior change.
  const instanceClient = React.useMemo(
    () => (props.cacheKey ? new QueryClient() : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.cacheKey],
  );

  return (
    <QueryClientProvider client={instanceClient ?? queryClient}>
      {MasterPatternViewInner<T>(props, ref)}
    </QueryClientProvider>
  );
}) as <T extends MasterPatternViewEntity>(
  props: MasterPatternViewProps<T> & { ref?: React.ForwardedRef<MasterPatternViewRef> }
) => React.ReactElement | null;
